import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  type AgentChatDock,
  type AgentChatPlacement,
  readChatPinned,
  readChatPlacement,
  readChatSideWidth,
  writeChatPinned,
  writeChatPlacement,
} from '@/shared/lib/agentChatPreferences';

import { ChatWorkspaceContext } from '../model';

type Dock = AgentChatDock;

export const useChatDocking = ({
  paneRef,
  isMaximized,
  isOpen,
  enabled,
  onDetach,
}: {
  paneRef: RefObject<HTMLElement | null>;
  isMaximized: boolean;
  isOpen: boolean;
  enabled: boolean;
  onDetach: (fromSideDock: boolean) => () => void;
}) => {
  const { reserve } = useContext(ChatWorkspaceContext);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [pinned, setPinned] = useState(readChatPinned);
  const togglePin = () => {
    // Compute the next value outside the updater: state updater functions
    // must stay pure (they may run twice under StrictMode).
    const next = !pinned;
    setPinned(next);
    writeChatPinned(next);
  };
  const [preferredWidth, setPreferredWidth] = useState(
    () => readChatSideWidth() ?? 400,
  );
  const canPin = viewportWidth >= 1000;
  const maxSideWidth = Math.max(
    Math.min(360, viewportWidth),
    Math.min(800, viewportWidth - (pinned && canPin ? 640 : 0)),
  );
  const sideWidth = Math.min(preferredWidth, maxSideWidth);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    const resize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      resizeCleanupRef.current?.();
    };
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem('agentChatSideWidth', String(preferredWidth));
    } catch {
      return;
    }
  }, [preferredWidth]);
  const [placement, setPlacement] =
    useState<AgentChatPlacement>(readChatPlacement);
  const [preview, setPreview] = useState<Dock | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const previousRect = useRef<DOMRect | null>(null);
  const animationRef = useRef<Animation | null>(null);
  const dragSizeRef = useRef({ width: 760, height: 520 });
  const dragPositionRef = useRef({ x: 0, y: 0 });
  const renderedDock = preview ?? (isDragging ? 'floating' : placement.dock);
  const isSideDocked = renderedDock === 'left' || renderedDock === 'right';
  // Pinning keeps the pane open across outside clicks in every dock —
  // side, bottom, and floating alike. Pinned is the default variant
  // (readChatPinned defaults to true); unpinning opts into the free,
  // auto-collapsing pane while the preference survives for later docks.
  const isPinned = pinned && canPin && !isDragging;
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    reserve(
      isOpen &&
        isPinned &&
        (placement.dock === 'left' || placement.dock === 'right')
        ? { side: placement.dock, width: sideWidth }
        : null,
    );
  }, [enabled, isOpen, isPinned, placement.dock, sideWidth, reserve]);
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    return () => reserve(null);
  }, [enabled, reserve]);

  const resizeSide = (width: number) =>
    setPreferredWidth(
      Math.max(Math.min(360, viewportWidth), Math.min(maxSideWidth, width)),
    );
  const onSideResize = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0 || !event.isPrimary || resizeCleanupRef.current) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const startX = event.clientX;
    const startWidth = sideWidth;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    const gesture = { width: startWidth, frame: 0 };
    const render = () => {
      gesture.frame = 0;
      resizeSide(gesture.width);
    };
    const move = (pointer: PointerEvent) => {
      if (pointer.pointerId !== event.pointerId) {
        return;
      }
      gesture.width =
        startWidth +
        (pointer.clientX - startX) * (placement.dock === 'left' ? 1 : -1);
      if (!gesture.frame) {
        gesture.frame = requestAnimationFrame(render);
      }
    };
    const cleanup = () => {
      cancelAnimationFrame(gesture.frame);
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', cancel);
      document.removeEventListener('keydown', keydown, true);
      window.removeEventListener('blur', cancel);
      if (handle.hasPointerCapture(event.pointerId)) {
        handle.releasePointerCapture(event.pointerId);
      }
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      resizeCleanupRef.current = null;
    };
    const up = (pointer: PointerEvent) => {
      if (pointer.pointerId !== event.pointerId) {
        return;
      }
      move(pointer);
      cleanup();
      render();
    };
    const cancel = () => {
      cleanup();
      resizeSide(startWidth);
    };
    const keydown = (key: KeyboardEvent) => {
      if (key.key !== 'Escape') {
        return;
      }
      key.preventDefault();
      key.stopImmediatePropagation();
      cancel();
    };
    resizeCleanupRef.current = cleanup;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', cancel);
    document.addEventListener('keydown', keydown, true);
    window.addEventListener('blur', cancel);
  };

  useEffect(() => () => cleanupRef.current?.(), []);
  useEffect(() => {
    if (isDragging) {
      return;
    }
    writeChatPlacement(placement);
  }, [placement, isDragging]);

  useLayoutEffect(() => {
    const pane = paneRef.current;
    const before = previousRect.current;
    previousRect.current = null;
    if (
      !pane ||
      !before ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    animationRef.current?.cancel();
    const after = pane.getBoundingClientRect();
    animationRef.current = pane.animate(
      [
        {
          transform: `translate(${before.x - after.x}px, ${before.y - after.y}px) scale(${before.width / after.width}, ${before.height / after.height})`,
          transformOrigin: 'top left',
        },
        { transform: 'none', transformOrigin: 'top left' },
      ],
      { duration: 200, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
    );
  }, [renderedDock, paneRef]);

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (
      event.button !== 0 ||
      !event.isPrimary ||
      cleanupRef.current !== null ||
      (event.target as HTMLElement).closest(
        'button, input, textarea, a, [role="button"]',
      )
    ) {
      return;
    }
    const pane = paneRef.current;
    if (!pane) {
      return;
    }
    event.preventDefault();
    previousRect.current = null;
    const header = event.currentTarget;
    header.setPointerCapture(event.pointerId);
    const start = { x: event.clientX, y: event.clientY };
    const rect = pane.getBoundingClientRect();
    const origin = placement;
    const gesture = {
      started: false,
      restore: null as (() => void) | null,
      preview: null as Dock | null,
      position: placement.position,
      frame: 0,
      pointer: start,
      leftDockArmed: origin.dock !== 'left',
      rightDockArmed: origin.dock !== 'right',
      bottomDockArmed: origin.dock !== 'bottom',
      highestPointerY: start.y,
    };
    const width = Math.min(
      isSideDocked || isMaximized ? 760 : rect.width,
      window.innerWidth - 48,
    );
    const offsetX = Math.min(
      width - 24,
      Math.max(24, ((start.x - rect.x) / rect.width) * width),
    );
    const offsetY = start.y - rect.y;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    const viewport = {
      width: document.documentElement.clientWidth,
      height: window.innerHeight,
    };
    const height = Math.min(
      isSideDocked || isMaximized ? 520 : rect.height,
      viewport.height,
    );
    const renderPosition = (applyTranslate = true) => {
      gesture.frame = 0;
      const pointer = gesture.pointer;
      gesture.position = {
        x: Math.max(0, Math.min(viewport.width - width, pointer.x - offsetX)),
        y: Math.max(0, Math.min(viewport.height - height, pointer.y - offsetY)),
      };
      const edgeDistance = -12;
      const releaseDistance = 24;
      const projectedLeft = pointer.x - offsetX;
      const projectedRight = projectedLeft + width;
      const projectedBottom = pointer.y - offsetY + height;
      gesture.highestPointerY = Math.min(gesture.highestPointerY, pointer.y);
      if (projectedBottom < viewport.height - releaseDistance) {
        gesture.bottomDockArmed = true;
      }
      if (projectedLeft > releaseDistance) {
        gesture.leftDockArmed = true;
      }
      if (projectedRight < viewport.width - releaseDistance) {
        gesture.rightDockArmed = true;
      }
      const nearLeft =
        pointer.x <= 12 ||
        (gesture.leftDockArmed &&
          (gesture.preview === 'left' || start.x - pointer.x >= 48) &&
          projectedLeft <=
            (gesture.preview === 'left' ? releaseDistance : edgeDistance));
      const nearRight =
        pointer.x >= viewport.width - 12 ||
        (gesture.rightDockArmed &&
          (gesture.preview === 'right' || pointer.x - start.x >= 48) &&
          projectedRight >=
            viewport.width -
              (gesture.preview === 'right' ? releaseDistance : edgeDistance));
      const nearBottom =
        pointer.y >= viewport.height - 12 ||
        (gesture.bottomDockArmed &&
          projectedBottom >=
            viewport.height -
              (gesture.preview === 'bottom' ? releaseDistance : edgeDistance) &&
          (gesture.preview === 'bottom' ||
            pointer.y - gesture.highestPointerY >= 32));
      const nextPreview: Dock | null = nearLeft
        ? 'left'
        : nearRight
          ? 'right'
          : nearBottom
            ? 'bottom'
            : null;
      if (nextPreview !== gesture.preview) {
        previousRect.current = pane.getBoundingClientRect();
        gesture.preview = nextPreview;
        setPreview(nextPreview);
      }
      if (nextPreview === null) {
        dragPositionRef.current = gesture.position;
        if (applyTranslate) {
          pane.style.translate = `${gesture.position.x}px ${gesture.position.y}px`;
        }
      }
    };
    const move = (pointer: PointerEvent) => {
      if (pointer.pointerId !== event.pointerId) {
        return;
      }
      gesture.pointer = { x: pointer.clientX, y: pointer.clientY };
      if (!gesture.started) {
        if (
          Math.hypot(pointer.clientX - start.x, pointer.clientY - start.y) <
          (origin.dock === 'floating' ? 4 : 56)
        ) {
          return;
        }
        gesture.started = true;
        dragSizeRef.current = { width, height };
        gesture.restore = onDetach(isSideDocked);
        renderPosition(false);
        setIsDragging(true);
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
        return;
      }
      if (!gesture.frame) {
        gesture.frame = requestAnimationFrame(() => renderPosition());
      }
    };
    const cleanup = () => {
      cancelAnimationFrame(gesture.frame);
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', cancel);
      document.removeEventListener('keydown', keydown, true);
      window.removeEventListener('blur', cancel);
      if (header.hasPointerCapture(event.pointerId)) {
        header.releasePointerCapture(event.pointerId);
      }
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      cleanupRef.current = null;
    };
    const finish = (cancelled: boolean) => {
      cleanup();
      if (!gesture.started) {
        return;
      }
      if (cancelled) {
        previousRect.current = pane.getBoundingClientRect();
        gesture.restore?.();
      }
      setPlacement(
        cancelled
          ? origin
          : { dock: gesture.preview ?? 'floating', position: gesture.position },
      );
      setPreview(null);
      setIsDragging(false);
    };
    const up = (pointer: PointerEvent) => {
      if (pointer.pointerId !== event.pointerId) {
        return;
      }
      if (gesture.started) {
        cancelAnimationFrame(gesture.frame);
        gesture.pointer = { x: pointer.clientX, y: pointer.clientY };
        renderPosition(false);
      }
      finish(false);
    };
    const cancel = () => finish(true);
    const keydown = (key: KeyboardEvent) => {
      if (key.key !== 'Escape') {
        return;
      }
      key.preventDefault();
      key.stopImmediatePropagation();
      cancel();
    };
    cleanupRef.current = cleanup;
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', cancel);
    document.addEventListener('keydown', keydown, true);
    window.addEventListener('blur', cancel);
  };

  const style: CSSProperties = isSideDocked
    ? {
        /* Side-docked (pinned or overlay): the pane sits flush at the
           layout viewport edge. No page scroller reserves a scrollbar
           strip in any state, so the page lays out identically pinned and
           unpinned. */
        left: renderedDock === 'left' ? 0 : 'auto',
        right: renderedDock === 'right' ? 0 : 'auto',
        top: 0,
        bottom: 0,
        width: sideWidth,
        zIndex: 110,
        height: '100dvh',
        maxHeight: '100dvh',
        maxWidth: '100%',
        borderRadius: 0,
        transform: 'none',
        translate: 'none',
        animation: 'none',
        transition: 'none',
      }
    : renderedDock === 'floating'
      ? isDragging
        ? {
            left: 0,
            top: 0,
            bottom: 'auto',
            zIndex: 110,
            width: dragSizeRef.current.width,
            height: dragSizeRef.current.height,
            maxHeight: dragSizeRef.current.height,
            translate: `${dragPositionRef.current.x}px ${dragPositionRef.current.y}px`,
            transform: 'none',
            willChange: 'translate',
            animation: 'none',
            transition: 'none',
          }
        : {
            left: `clamp(0px, ${placement.position.x}px, calc(100% - ${paneRef.current?.offsetWidth ?? 760}px))`,
            top: `clamp(0px, ${placement.position.y}px, calc(100dvh - min(240px, 100dvh)))`,
            bottom: 'auto',
            zIndex: 110,
            transform: 'none',
            translate: 'none',
            animation: 'none',
            transition: 'none',
            maxHeight: `calc(100dvh - ${Math.max(0, Math.min(placement.position.y, window.innerHeight - Math.min(240, window.innerHeight)))}px)`,
          }
      : {
          translate: '-50% 0',
          transform: 'none',
          animation: 'none',
          transition: 'none',
        };
  const triggerStyle: CSSProperties | undefined =
    placement.dock === 'left' || placement.dock === 'right'
      ? {
          left: placement.dock === 'left' ? 16 : 'auto',
          right: placement.dock === 'right' ? 16 : 'auto',
          top: '50%',
          bottom: 'auto',
          transform: 'translateY(-50%)',
          animation: 'none',
          maxWidth: 'calc(100vw - 32px)',
        }
      : undefined;
  const setPosition = useCallback(
    (position: AgentChatPlacement['position']) => {
      setPlacement((current) => ({ ...current, position }));
    },
    [],
  );
  return {
    dock: isDragging ? 'floating' : placement.dock,
    preview,
    isDragging,
    isSideDocked,
    isPinned,
    canPin,
    pinned,
    sideWidth,
    maxSideWidth,
    togglePin,
    onSideResize,
    resizeSide,
    style,
    triggerStyle,
    onPointerDown,
    setPosition,
  };
};
